/**
 * Product Features Service
 * API service for managing product features (FashSnap, FashPlay, FashMatch)
 */

import { authenticatedFetch, API_BASE_URL } from '../config/api';

export interface ProductMetrics {
  totalUsage: number;
  monthlyUsage: number;
  dailyTrend: number[];
  quota: {
    used: number;
    total: number;
    remaining: number;
  };
}

export interface ProductFeature {
  code: 'fashsnap' | 'fashplay' | 'fashmatch';
  name: string;
  description: string;
  enabled: boolean;
  metrics?: ProductMetrics;
}

export interface ProductFeaturesResponse {
  success: boolean;
  products: ProductFeature[];
}

export class ProductFeaturesService {
  /**
   * Get all product features for a brand
   */
  static async getProductFeatures(brandId: string): Promise<ProductFeature[]> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/features`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ProductFeaturesResponse = await response.json();
      return data.products;
    } catch (error) {
      console.error('Failed to fetch product features:', error);
      throw error;
    }
  }

  /**
   * Enable a product for a brand
   */
  static async enableProduct(
    brandId: string,
    productCode: string,
    options?: { baseQuota?: number; basePrice?: number }
  ): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/products/${productCode}/enable`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(options || {}),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to enable product ${productCode}:`, error);
      throw error;
    }
  }

  /**
   * Disable a product for a brand
   */
  static async disableProduct(brandId: string, productCode: string): Promise<void> {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/brands/${brandId}/products/${productCode}/disable`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to disable product ${productCode}:`, error);
      throw error;
    }
  }
}
